import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import type { ReportDefinition, ReportFilters } from '../types/report';

interface ReportCardProps {
  report: ReportDefinition;
  filters: ReportFilters;
  onDownloadExcel: () => void;
  onDownloadPdf: () => void;
  onOpenPdf: () => void;
  isDownloading: boolean;
}

/**
 * Card component for displaying a report with download buttons for Excel and PDF
 */
export function ReportCard({
  report,
  filters,
  onDownloadExcel,
  onDownloadPdf,
  onOpenPdf,
  isDownloading,
}: ReportCardProps) {
  const hasDateRange = filters.startDate && filters.endDate;
  const isDisabled = report.requiresDateRange && !hasDateRange;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="text-lg">{report.name}</CardTitle>
        <CardDescription>{report.description}</CardDescription>
      </CardHeader>
      <CardContent className="mt-auto space-y-2">
        <div className="flex gap-2">
          <Button
            onClick={onDownloadExcel}
            disabled={isDisabled || isDownloading}
            variant="default"
            className="flex-1"
            data-testid={`download-${report.id}-excel`}
          >
            {isDownloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="mr-2 h-4 w-4" />
            )}
            Excel
          </Button>
          <Button
            onClick={onOpenPdf}
            disabled={isDisabled || isDownloading}
            variant="secondary"
            className="flex-1 bg-secondary-subtle hover:bg-secondary-subtle/90"
            data-testid={`view-${report.id}-pdf`}
          >
            {isDownloading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <FileText className="mr-2 h-4 w-4" />
            )}
            View PDF
          </Button>
        </div>
        <Button
          onClick={onDownloadPdf}
          disabled={isDisabled || isDownloading}
          variant="outline"
          className="w-full"
          data-testid={`download-${report.id}-pdf`}
        >
          {isDownloading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Download PDF
        </Button>
        {isDisabled && (
          <p className="text-xs text-muted-foreground text-center">
            Select a date range to download this report
          </p>
        )}
      </CardContent>
    </Card>
  );
}
